import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { CityInfo } from './cityData';
import { useLockBody } from './useLockBody';

const spring = { type: 'spring' as const, stiffness: 380, damping: 32, mass: 0.8 };

type Props = { city: CityInfo | null; onClose: () => void };

// Basit focus trap
function useFocusTrap(enabled: boolean, containerRef: React.RefObject<HTMLDivElement>) {
  useEffect(() => {
    if (!enabled) return;
    const el = containerRef.current;
    if (!el) return;
    const focusables = el.querySelectorAll<HTMLElement>(
      'a,button,textarea,input,select,[tabindex]:not([tabindex="-1"])'
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    first?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') return;
      if (e.key !== 'Tab') return;
      if (focusables.length === 0) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled, containerRef]);
}

export default function CityModal({ city, onClose }: Props) {
  const shouldReduce = useReducedMotion();
  const open = Boolean(city);
  useLockBody(open);
  const sheetRef = useRef<HTMLDivElement>(null);
  useFocusTrap(open, sheetRef);

  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    if (open) document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && city && (
        <motion.div
          aria-hidden={false}
          className="fixed inset-0 z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: shouldReduce ? 0.1 : 0.2 }}
        >
          {/* Backdrop */}
          <button
            aria-label="Kapat"
            onClick={onClose}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          />

          {/* Sheet */}
          <motion.div
            role="dialog"
            aria-modal="true"
            ref={sheetRef}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            onDragEnd={(_: unknown, info: { offset: { y: number } }) => {
              if (info.offset.y > 120) onClose();
            }}
            className="relative max-w-4xl mx-4 md:mx-auto mt-6 md:mt-16 bg-[rgb(245,254,253)] text-[rgb(65,74,76)] rounded-2xl shadow-xl border border-white/40 max-h-[calc(100vh-4rem)] overflow-y-auto"
            initial={{ y: shouldReduce ? 0 : 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1, transition: { ...spring } }}
            exit={{ y: shouldReduce ? 0 : 40, opacity: 0, transition: { duration: 0.15 } }}
          >
            {/* Hero shared element */}
            <motion.div layoutId={`card-${city.slug}`}> 
              <motion.img
                layoutId={`img-${city.slug}`}
                src={city.cover}
                alt={city.name}
                className="w-full aspect-[16/9] object-cover"
              />
            </motion.div>

            <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <motion.h2
                  layoutId={`ttl-${city.slug}`}
                  tabIndex={-1}
                  className="text-2xl md:text-3xl font-bold mb-2"
                >
                  {city.name}
                </motion.h2>
                <div className="h-1 w-16 bg-[rgb(0,191,255)] rounded-full mb-4" />
                <ul className="list-disc ml-5 space-y-1 text-sm md:text-base">
                  {city.highlights.map((h) => (
                    <li key={h}>{h}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4 text-sm md:text-base">
                <section>
                  <h3 className="font-semibold mb-1">Top Attractions</h3>
                  <ul className="list-disc ml-5 space-y-1">
                    {city.topAttractions.map((a) => (
                      <li key={a}>{a}</li>
                    ))}
                  </ul>
                </section>
                <section>
                  <h3 className="font-semibold mb-1">Local Foods</h3>
                  <ul className="list-disc ml-5 space-y-1">
                    {city.localFoods.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </section>
                <section>
                  <h3 className="font-semibold mb-1">Best Season</h3>
                  <p>{city.bestSeason}</p>
                </section>
                <section>
                  <h3 className="font-semibold mb-1">1‑Day Plan</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div className="p-3 bg-white/60 rounded-lg border border-white/40">
                      <div className="text-xs text-gray-500">Sabah</div>
                      <div>{city.oneDayPlan.morning}</div>
                    </div>
                    <div className="p-3 bg-white/60 rounded-lg border border-white/40">
                      <div className="text-xs text-gray-500">Öğle</div>
                      <div>{city.oneDayPlan.noon}</div>
                    </div>
                    <div className="p-3 bg-white/60 rounded-lg border border-white/40">
                      <div className="text-xs text-gray-500">Akşam</div>
                      <div>{city.oneDayPlan.evening}</div>
                    </div>
                  </div>
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[rgb(0,191,255)] text-white text-sm">
                    ~ {city.oneDayPlan.budgetTRY.toLocaleString('tr-TR')} TL
                  </div>
                </section>

                <div className="pt-2 flex gap-2">
                  <a href="#" className="px-3 py-2 rounded-lg bg-[rgb(0,191,255)] text-white text-sm">Haritada Aç</a>
                  <a href="#" className="px-3 py-2 rounded-lg bg-white/70 border border-white/40 text-sm">Uçak</a>
                  <a href="#" className="px-3 py-2 rounded-lg bg-white/70 border border-white/40 text-sm">Otel</a>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) || null}
    </AnimatePresence>
  );
}
